import { useState, useEffect, useCallback, Fragment } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { FileText, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  fetchAuditLog,
  fetchAuditActions,
  exportAuditLogCsv,
} from '@/hooks/useAdmin';
import type { AuditLogItem } from '@uniticket/shared';

export function AuditLog() {
  const { t } = useTranslation('admin');
  const [logs, setLogs] = useState<AuditLogItem[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filterAction, setFilterAction] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const pageSize = 50;

  const loadData = useCallback(async () => {
    try {
      const params: Record<string, string> = {
        limit: String(pageSize),
        offset: String(page * pageSize),
      };
      if (filterAction) params.action = filterAction;
      if (filterEntityType) params.entityType = filterEntityType;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const [logRes, actionRes] = await Promise.all([
        fetchAuditLog(params),
        actions.length > 0 ? Promise.resolve({ data: actions }) : fetchAuditActions(),
      ]);
      setLogs(logRes.data);
      setTotal(logRes.meta.total);
      if (actions.length === 0) setActions(actionRes.data);
    } catch (err: any) {
      toast.error(err.message || t('auditLog.loadError'));
    } finally {
      setLoading(false);
    }
  }, [page, filterAction, filterEntityType, dateFrom, dateTo, actions.length, t]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleExport() {
    try {
      const params: Record<string, string> = {};
      if (filterAction) params.action = filterAction;
      if (filterEntityType) params.entityType = filterEntityType;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const blob = await exportAuditLogCsv(params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('auditLog.exported'));
    } catch (err: any) {
      toast.error(err.message || t('auditLog.exportError'));
    }
  }

  const totalPages = Math.ceil(total / pageSize);
  const entityTypes = [...new Set(logs.map((l) => l.entityType).filter(Boolean))] as string[];

  if (loading && logs.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">{t('auditLog.title')}</h2>
            <p className="text-sm text-slate-500">{t('auditLog.count', { count: total })}</p>
          </div>
        </div>
        <Button variant="outline" onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" />
          {t('auditLog.export')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={filterAction} onValueChange={(v) => { setFilterAction(v === 'ALL' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-52">
            <SelectValue placeholder={t('auditLog.filterAction')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t('auditLog.allActions')}</SelectItem>
            {actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterEntityType} onValueChange={(v) => { setFilterEntityType(v === 'ALL' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder={t('auditLog.filterEntityType')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t('auditLog.allEntityTypes')}</SelectItem>
            {entityTypes.map((et) => <SelectItem key={et} value={et}>{et}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            className="w-40"
          />
          <span className="text-slate-400">—</span>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            className="w-40"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="w-8 px-2"></th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('auditLog.cols.timestamp')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('auditLog.cols.user')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('auditLog.cols.action')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('auditLog.cols.entityType')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('auditLog.cols.entityId')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('auditLog.cols.ipAddress')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                    {t('auditLog.empty')}
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <Fragment key={log.id}>
                    <tr
                      className="hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                    >
                      <td className="px-2 text-center">
                        {expandedRow === log.id ? (
                          <ChevronUp className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-700 text-xs">{log.userDisplayName || '—'}</span>
                          <span className="text-xs text-slate-400">{log.userEmail || ''}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center text-xs font-semibold text-brand-purple bg-brand-rose/10 px-2 py-0.5 rounded-full">
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{log.entityType || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{log.entityId ? log.entityId.slice(0, 8) + '...' : '—'}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{log.ipAddress || '—'}</td>
                    </tr>
                    {expandedRow === log.id && (
                      <tr>
                        <td colSpan={7} className="px-8 py-4 bg-slate-50/80">
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-slate-500 uppercase">{t('auditLog.details')}</p>
                            <div className="bg-white rounded-lg border border-slate-200 p-3">
                              {Object.keys(log.details).length === 0 ? (
                                <p className="text-xs text-slate-400">{t('auditLog.noDetails')}</p>
                              ) : (
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                  {Object.entries(log.details).map(([key, value]) => (
                                    <div key={key} className="flex gap-2">
                                      <dt className="font-semibold text-slate-600">{key}:</dt>
                                      <dd className="text-slate-500 break-all">
                                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                      </dd>
                                    </div>
                                  ))}
                                </dl>
                              )}
                            </div>
                            {log.userAgent && (
                              <p className="text-xs text-slate-400 truncate" title={log.userAgent}>
                                <span className="font-semibold">User Agent:</span> {log.userAgent}
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            {t('auditLog.showing', { from: page * pageSize + 1, to: Math.min((page + 1) * pageSize, total), total })}
          </p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
